<template>
  <div class="ovw">
    <!-- ===== Stat tiles ===== -->
    <div class="ovw__stats">
      <div class="ovw__tile">
        <div class="ovw__tile-head">
          <span class="ovw__tile-label">{{ $t("overview.level") }}</span>
          <button
            v-if="allowLevelEdit"
            type="button"
            class="ovw__edit"
            :aria-label="$t('overview.editLevel')"
            @click="openLevelDialog"
          >
            <v-icon size="15" color="#A826E1">mdi-pencil-outline</v-icon>
          </button>
        </div>
        <div class="ovw__tile-value">{{ displayLevel(classData.level) }}</div>
        <div v-if="allowLevelEdit" class="ovw__tile-helper">
          {{ $t("overview.levelHelper") }}
        </div>
      </div>

      <div class="ovw__tile">
        <div class="ovw__tile-head">
          <span class="ovw__tile-label">{{ $t("overview.format") }}</span>
          <button
            v-if="allowFormatEdit"
            type="button"
            class="ovw__edit"
            :aria-label="$t('overview.editFormat')"
            @click="openFormatDialog"
          >
            <v-icon size="15" color="#A826E1">mdi-pencil-outline</v-icon>
          </button>
        </div>
        <div class="ovw__tile-value">{{ displayFormat(classData.format) }}</div>
        <div v-if="allowFormatEdit" class="ovw__tile-helper">
          {{ $t("overview.formatHelper") }}
        </div>
      </div>

      <div class="ovw__tile">
        <div class="ovw__tile-head">
          <span class="ovw__tile-label">{{ $t("overview.frequency") }}</span>
          <button
            v-if="allowFrequencyEdit"
            type="button"
            class="ovw__edit"
            :aria-label="$t('overview.editFrequency')"
            @click="openFrequencyDialog"
          >
            <v-icon size="15" color="#A826E1">mdi-pencil-outline</v-icon>
          </button>
        </div>
        <div class="ovw__tile-value">
          {{ displayFrequency(classData.frequency) }}
        </div>
        <div v-if="allowFrequencyEdit" class="ovw__tile-helper">
          {{ $t("overview.frequencyHelper") }}
        </div>
      </div>
    </div>

    <!-- ===== Turma Kids ===== -->
    <!-- Marcar como Kids muda quem recebe as aulas de conversacao: as sessoes
         forKids passam a valer para os alunos ativos desta turma. -->
    <section v-if="allowKidsEdit" class="ovw__kids">
      <div class="ovw__kids-copy">
        <div class="ovw__kids-title">
          <v-icon size="18" color="#A826E1" class="mr-2">mdi-teddy-bear</v-icon>
          {{ $t("overview.kidsTitle") }}
        </div>
        <p class="ovw__kids-text">
          {{ $t("overview.kidsText") }}
        </p>
      </div>

      <v-switch
        :input-value="kidsValue"
        :loading="kidsBusy"
        :disabled="kidsBusy"
        inset
        hide-details
        color="#A826E1"
        class="ovw__kids-switch ma-0 pa-0"
        :aria-label="
          kidsValue ? $t('overview.kidsRemove') : $t('overview.kidsMark')
        "
        @change="submitKidsUpdate"
      />
    </section>

    <!-- ===== Cronograma + professor ===== -->
    <div class="ovw__cols">
      <section class="ovw__panel">
        <div class="ovw__panel-title">
          <v-icon size="18" color="#A826E1" class="mr-2"
            >mdi-calendar-month-outline</v-icon
          >
          {{ $t("overview.schedule") }}
        </div>

        <div v-if="normalizedTimes.length" class="ovw__slots">
          <div
            v-for="(time, index) in normalizedTimes"
            :key="`${time.weekDay}-${time.start}-${index}`"
            class="ovw__slot"
          >
            <span class="ovw__slot-day">{{ weekDayLabel(time.weekDay) }}</span>
            <span class="ovw__slot-time"
              >{{ time.start }}<span class="ovw__slot-dash">–</span
              >{{ time.end }}</span
            >
          </div>
        </div>
        <div v-else class="ovw__muted">
          {{ $t("overview.noSchedule") }}
        </div>
      </section>

      <section class="ovw__panel">
        <div class="ovw__panel-title">
          <v-icon size="18" color="#A826E1" class="mr-2"
            >mdi-account-tie-outline</v-icon
          >
          {{ $t("overview.teacher") }}
        </div>

        <div class="ovw__teacher">
          <span class="ovw__avatar ovw__avatar--lg">
            <img v-if="teacherAvatar" :src="teacherAvatar" :alt="teacherName" />
            <span v-else>{{ teacherInitials }}</span>
          </span>
          <div class="ovw__teacher-info">
            <div class="ovw__teacher-name">{{ teacherName }}</div>
            <div class="ovw__muted">{{ teacherEmail }}</div>
          </div>
        </div>
      </section>
    </div>

    <!-- ===== Alunos ===== -->
    <section class="ovw__members">
      <div class="ovw__members-head">
        {{ membersTitle || theme.membersTitle || $t("overview.students") }}
        <span class="ovw__members-count">{{ safeMembers.length }}</span>
      </div>

      <slot name="members-content" :members="safeMembers">
        <div v-if="safeMembers.length" class="ovw__members-grid">
          <div
            v-for="(member, index) in safeMembers"
            :key="member.id || `${member.email}-${index}`"
            class="ovw__member"
          >
            <span class="ovw__avatar">
              <img
                v-if="member.avatar"
                :src="member.avatar"
                :alt="member.name"
              />
              <span v-else>{{ memberInitials(member.name) }}</span>
            </span>
            <div class="ovw__member-info">
              <div class="ovw__member-name">
                {{ member.name || $t("memberCard.unnamed") }}
              </div>
              <div v-if="member.email" class="ovw__muted ovw__member-email">
                {{ member.email }}
              </div>
            </div>
            <span class="ovw__member-level">{{
              displayLevel(member.level)
            }}</span>
          </div>
        </div>

        <div v-else class="ovw__muted">
          {{ membersEmptyText || $t("overview.noMembers") }}
        </div>
      </slot>
    </section>

    <!-- ===== Dialog: nível da turma ===== -->
    <v-dialog v-if="allowLevelEdit" v-model="levelDialog" max-width="440">
      <v-card class="ovw-dialog" rounded="lg">
        <v-card-title class="text-h6">{{
          $t("overview.levelTitle")
        }}</v-card-title>
        <v-card-text>
          <div class="ovw-dialog__hint">
            {{ $t("overview.levelHint") }}
          </div>

          <v-select
            v-model="selectedLevel"
            :items="levelOptions"
            item-text="text"
            item-value="value"
            :label="$t('overview.levelLabel')"
            color="#A826E1"
            item-color="#A826E1"
            outlined
            dense
            hide-details="auto"
          />

          <div v-if="safeMembers.length" class="mt-5">
            <div class="ovw-dialog__hint">
              {{ $t("overview.cascade") }}
            </div>
            <v-checkbox
              v-for="member in safeMembers"
              :key="member.id"
              v-model="cascadeStudentIds"
              :label="member.name"
              :value="member.id"
              color="#A826E1"
              dense
              hide-details
              class="mt-1"
            />
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="closeLevelDialog">{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn
            dark
            rounded
            color="#A826E1"
            :loading="levelBusy"
            :disabled="
              selectedLevel === null ||
              (+selectedLevel === +classData.level &&
                cascadeStudentIds.length === 0)
            "
            @click="submitLevelUpdate"
          >
            {{ $t("memberCard.saveLevel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Dialog: formato da turma ===== -->
    <v-dialog v-if="allowFormatEdit" v-model="formatDialog" max-width="440">
      <v-card class="ovw-dialog" rounded="lg">
        <v-card-title class="text-h6">{{
          $t("overview.formatTitle")
        }}</v-card-title>
        <v-card-text>
          <div class="ovw-dialog__hint">
            {{ $t("overview.formatHint") }}
            {{ capacityHint }}
          </div>

          <!-- Formatos menores que a turma atual ficam desabilitados: a regra e
               conhecida aqui, entao nao vale deixar o professor escolher para
               descobrir pelo erro do servidor. -->
          <v-select
            v-model="selectedFormat"
            :items="formatChoices"
            item-text="text"
            item-value="value"
            item-disabled="disabled"
            :label="$t('overview.formatLabel')"
            color="#A826E1"
            item-color="#A826E1"
            outlined
            dense
            hide-details="auto"
          />
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="closeFormatDialog">{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn
            dark
            rounded
            color="#A826E1"
            :loading="formatBusy"
            :disabled="
              selectedFormat === null ||
              +selectedFormat === +classData.format ||
              selectedFormat < activeMembersCount
            "
            @click="submitFormatUpdate"
          >
            {{ $t("overview.saveFormat") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== Dialog: frequência da turma ===== -->
    <v-dialog
      v-if="allowFrequencyEdit"
      v-model="frequencyDialog"
      max-width="440"
    >
      <v-card class="ovw-dialog" rounded="lg">
        <v-card-title class="text-h6">{{
          $t("overview.frequencyTitle")
        }}</v-card-title>
        <v-card-text>
          <div class="ovw-dialog__hint">
            {{ $t("overview.frequencyHint") }}
          </div>

          <v-select
            v-model="selectedFrequency"
            :items="frequencyOptions"
            item-text="text"
            item-value="value"
            :label="$t('overview.frequencyLabel')"
            color="#A826E1"
            item-color="#A826E1"
            outlined
            dense
            hide-details="auto"
            class="mt-4"
          />

          <!-- A grade sugere uma frequência, mas nao manda: a quinzenal tem um
               dia só e ainda assim nao e "1x por semana". Por isso avisa em vez
               de bloquear. -->
          <div v-if="frequencyMismatch" class="ovw-dialog__warn">
            <v-icon size="16" color="#B5421E" class="mr-1"
              >mdi-alert-outline</v-icon
            >
            {{
              $tc("overview.mismatch", scheduleDayCount, {
                count: scheduleDayCount,
                suggested: displayFrequency(suggestedFrequency),
              })
            }}
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="closeFrequencyDialog">{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn
            dark
            rounded
            color="#A826E1"
            :loading="frequencyBusy"
            :disabled="
              selectedFrequency === null ||
              +selectedFrequency === +classData.frequency
            "
            @click="submitFrequencyUpdate"
          >
            {{ $t("overview.saveFrequency") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ENGLISH_LEVELS,
  GROUP_FORMAT_OPTIONS,
  GROUP_FREQUENCY_OPTIONS,
  WEEK_DAY_ITEMS,
  displayGroupFormat,
  displayGroupFrequency,
  displayGroupLevel,
  frequencyFromSchedule,
  initialsFromName,
} from "@/shared/classPresentation";

export default {
  name: "ClassOverviewCard",

  props: {
    classData: {
      type: Object,
      required: true,
    },
    normalizedTimes: {
      type: Array,
      default: () => [],
    },
    members: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: Object,
      required: true,
    },
    membersTitle: {
      type: String,
      default: "",
    },
    membersEmptyText: {
      type: String,
      default: "",
    },
    showBilling: {
      type: Boolean,
      default: false,
    },
    allowLevelEdit: {
      type: Boolean,
      default: false,
    },
    hideHero: {
      type: Boolean,
      default: false,
    },
    levelOptions: {
      type: Array,
      default: () => ENGLISH_LEVELS,
    },
    levelBusy: {
      type: Boolean,
      default: false,
    },
    allowFormatEdit: {
      type: Boolean,
      default: false,
    },
    formatOptions: {
      type: Array,
      default: () => GROUP_FORMAT_OPTIONS,
    },
    formatBusy: {
      type: Boolean,
      default: false,
    },
    allowFrequencyEdit: {
      type: Boolean,
      default: false,
    },
    frequencyOptions: {
      type: Array,
      default: () => GROUP_FREQUENCY_OPTIONS,
    },
    frequencyBusy: {
      type: Boolean,
      default: false,
    },
    allowKidsEdit: {
      type: Boolean,
      default: false,
    },
    kidsBusy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      levelDialog: false,
      selectedLevel: null,
      cascadeStudentIds: [],
      formatDialog: false,
      selectedFormat: null,
      frequencyDialog: false,
      selectedFrequency: null,
      kidsValue: false,
    };
  },

  watch: {
    "classData.level": {
      immediate: true,
      handler(level) {
        this.selectedLevel =
          level === undefined || level === null ? null : +level;
      },
    },
    "classData.format": {
      immediate: true,
      handler(format) {
        this.selectedFormat =
          format === undefined || format === null ? null : +format;
      },
    },
    "classData.frequency": {
      immediate: true,
      handler(frequency) {
        this.selectedFrequency =
          frequency === undefined || frequency === null ? null : +frequency;
      },
    },
    "classData.forKids": {
      immediate: true,
      handler(forKids) {
        this.kidsValue = !!forKids;
      },
    },
    // Se a gravacao falha, `classData.forKids` nao muda e o v-switch ficaria
    // virado para o estado que nao foi salvo. Ao fim de cada tentativa o valor
    // volta a seguir a turma -- no sucesso isso ja e o novo estado.
    kidsBusy(busy, wasBusy) {
      if (wasBusy && !busy) {
        this.kidsValue = !!this.classData.forKids;
      }
    },
  },

  computed: {
    safeMembers() {
      return Array.isArray(this.members) ? this.members : [];
    },
    teacherName() {
      return (
        this.classData?.teacher?.full_name ||
        this.classData?.teacher?.name ||
        this.$t("overview.noTeacher")
      );
    },
    teacherEmail() {
      return this.classData?.teacher?.email || this.$t("memberCard.noEmail");
    },
    teacherAvatar() {
      return this.classData?.teacher?.avatar || "";
    },
    teacherInitials() {
      return initialsFromName(this.teacherName);
    },
    activeMembersCount() {
      return this.safeMembers.length;
    },
    // O valor do formato e a propria capacidade (Dupla = 2 alunos), entao a
    // comparacao com o total de ativos e direta.
    formatChoices() {
      return this.formatOptions.map((option) => {
        const tooSmall = option.value < this.activeMembersCount;
        return {
          ...option,
          disabled: tooSmall,
          text: tooSmall
            ? this.$t("overview.tooSmall", {
                format: option.text,
                count: this.activeMembersCount,
              })
            : option.text,
        };
      });
    },
    capacityHint() {
      if (!this.activeMembersCount) return "";
      return this.activeMembersCount === 1
        ? this.$t("overview.capacityOne")
        : this.$t("overview.capacityMany", { count: this.activeMembersCount });
    },
    scheduleDayCount() {
      return new Set(this.normalizedTimes.map((time) => +time.weekDay)).size;
    },
    suggestedFrequency() {
      return frequencyFromSchedule(
        this.classData.weekDays,
        this.classData.hours
      );
    },
    // Quinzenal nunca conflita: por definicao ela nao sai da contagem de dias.
    frequencyMismatch() {
      if (this.selectedFrequency === null) return false;
      if (+this.selectedFrequency === 4) return false;
      if (this.suggestedFrequency === null) return false;
      return +this.selectedFrequency !== this.suggestedFrequency;
    },
  },

  methods: {
    displayLevel(level) {
      return displayGroupLevel(level);
    },
    displayFormat(format) {
      return displayGroupFormat(format);
    },
    displayFrequency(frequency) {
      return displayGroupFrequency(frequency);
    },
    memberInitials(name) {
      return initialsFromName(name) || "AL";
    },
    weekDayLabel(weekDay) {
      return (
        WEEK_DAY_ITEMS.find((item) => item.value === +weekDay)?.text || "-"
      );
    },
    openLevelDialog() {
      this.selectedLevel =
        this.classData.level === undefined || this.classData.level === null
          ? null
          : +this.classData.level;
      this.cascadeStudentIds = [];
      this.levelDialog = true;
    },
    closeLevelDialog() {
      this.levelDialog = false;
      this.selectedLevel =
        this.classData.level === undefined || this.classData.level === null
          ? null
          : +this.classData.level;
    },
    submitLevelUpdate() {
      const levelUnchanged = +this.selectedLevel === +this.classData.level;
      if (
        this.selectedLevel === null ||
        (levelUnchanged && this.cascadeStudentIds.length === 0)
      ) {
        return;
      }

      this.$emit("update-level", {
        level: +this.selectedLevel,
        studentIds: [...this.cascadeStudentIds],
      });
      this.levelDialog = false;
    },
    openFormatDialog() {
      this.selectedFormat =
        this.classData.format === undefined || this.classData.format === null
          ? null
          : +this.classData.format;
      this.formatDialog = true;
    },
    closeFormatDialog() {
      this.formatDialog = false;
      this.selectedFormat =
        this.classData.format === undefined || this.classData.format === null
          ? null
          : +this.classData.format;
    },
    submitFormatUpdate() {
      if (
        this.selectedFormat === null ||
        +this.selectedFormat === +this.classData.format ||
        this.selectedFormat < this.activeMembersCount
      ) {
        return;
      }

      this.$emit("update-format", +this.selectedFormat);
      this.formatDialog = false;
    },
    openFrequencyDialog() {
      this.selectedFrequency =
        this.classData.frequency === undefined ||
        this.classData.frequency === null
          ? null
          : +this.classData.frequency;
      this.frequencyDialog = true;
    },
    closeFrequencyDialog() {
      this.frequencyDialog = false;
      this.selectedFrequency =
        this.classData.frequency === undefined ||
        this.classData.frequency === null
          ? null
          : +this.classData.frequency;
    },
    submitFrequencyUpdate() {
      if (
        this.selectedFrequency === null ||
        +this.selectedFrequency === +this.classData.frequency
      ) {
        return;
      }

      this.$emit("update-frequency", +this.selectedFrequency);
      this.frequencyDialog = false;
    },
    submitKidsUpdate(value) {
      this.kidsValue = !!value;
      this.$emit("update-kids", !!value);
    },
  },
};
</script>

<style scoped>
.ovw {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #faf7fc);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --grape: var(--dk-grape, #a826e1);
  --grape-soft: rgba(168, 38, 225, 0.1);
  color: var(--ink);
}

/* ---------- Stat tiles ---------- */
.ovw__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}
.ovw__tile {
  padding: 16px 18px;
  border-radius: 16px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.ovw__tile-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ovw__tile-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--muted);
}
.ovw__edit {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  border: none;
  background: var(--grape-soft);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.ovw__edit:focus-visible {
  outline: 2px solid var(--grape);
  outline-offset: 2px;
}
.ovw__tile-value {
  margin-top: 8px;
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--ink);
}
.ovw__tile-helper {
  margin-top: 8px;
  font-size: 0.74rem;
  color: var(--muted);
  line-height: 1.4;
}

/* ---------- Cronograma + professor ---------- */
.ovw__cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 14px;
}
.ovw__panel {
  padding: 20px;
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
}
.ovw__panel-title {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--ink);
  margin-bottom: 14px;
}
.ovw__muted {
  font-size: 0.85rem;
  color: var(--muted);
  line-height: 1.45;
}
.ovw__slots {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ovw__slot {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ovw__slot-day {
  flex: 0 0 auto;
  min-width: 96px;
  padding: 4px 12px;
  border-radius: 8px;
  background: var(--grape-soft);
  color: var(--grape);
  font-size: 0.76rem;
  font-weight: 700;
}
.ovw__slot-time {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.ovw__slot-dash {
  color: var(--muted);
  margin: 0 3px;
}
.ovw__teacher {
  display: flex;
  align-items: center;
  gap: 14px;
}
.ovw__teacher-info {
  min-width: 0;
}
.ovw__teacher-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--ink);
}

/* ---------- Avatares ---------- */
.ovw__avatar {
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    160deg,
    var(--dk-grape, #c56eeb),
    var(--dk-grape, #a826e1)
  );
  color: #fff;
  font-size: 0.8rem;
  font-weight: 800;
}
.ovw__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ovw__avatar--lg {
  width: 56px;
  height: 56px;
}

/* ---------- Alunos ---------- */
.ovw__members {
  margin-top: 22px;
  padding-top: 22px;
  border-top: 1px solid var(--line);
}
.ovw__members-head {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--ink);
  margin-bottom: 14px;
}
.ovw__members-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--grape-soft);
  color: var(--grape);
  font-size: 0.78rem;
  font-weight: 800;
}
.ovw__members-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.ovw__member {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.ovw__member-info {
  flex: 1 1 auto;
  min-width: 0;
}
.ovw__member-name {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ovw__member-email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ovw__member-level {
  flex: 0 0 auto;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--grape);
}

/* ---------- Dialog ---------- */
.ovw-dialog__hint {
  font-size: 0.82rem;
  color: var(--muted);
  margin-bottom: 14px;
  line-height: 1.45;
}

/* Aviso, nao erro: a grade sugere uma frequencia, mas a escolha e legitima. */
.ovw-dialog__warn {
  display: flex;
  align-items: flex-start;
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 93, 42, 0.09);
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--dk-ember-deep, #b5421e);
}

/* ---------- Turma Kids ---------- */
.ovw__kids {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 16px;
  padding: 16px 20px;
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
}
.ovw__kids-copy {
  min-width: 0;
}
.ovw__kids-title {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--ink);
}
.ovw__kids-text {
  margin: 6px 0 0;
  max-width: 56ch;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--muted);
}
.ovw__kids-switch {
  flex: 0 0 auto;
}

@media (max-width: 599px) {
  .ovw__kids {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
}

@media (max-width: 720px) {
  .ovw__stats {
    grid-template-columns: 1fr;
  }
  .ovw__cols {
    grid-template-columns: 1fr;
  }
  .ovw__members-grid {
    grid-template-columns: 1fr;
  }
}
</style>
